import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import MsgModal from "./MsgModal"
import { displayedMessage, truncate } from "./messageHelpers"

// Defensive: reorder results to match the input order. The service is
// supposed to do this, but a stale/buggy backend deploy shouldn't break us.
const orderResults = (results, inputOrder) => {
  if (!Array.isArray(results)) return []
  if (!Array.isArray(inputOrder) || inputOrder.length === 0) return results
  const map = new Map()
  results.forEach((r) => map.set(r.order_number, r))
  const out = []
  const placed = new Set()
  inputOrder.forEach((on) => {
    const r = map.get(on)
    if (r) {
      out.push(r)
      placed.add(on)
    }
  })
  // append any extras the backend returned that we didn't ask for (shouldn't happen)
  results.forEach((r) => {
    if (!placed.has(r.order_number)) out.push(r)
  })
  return out
}

const outcomeIcon = (row) => {
  if (row.outcome === "ok" && (row.action === "completed" || !row.action)) {
    return { symbol: "✓", className: "text-green-600", label: "ok" }
  }
  if (row.outcome === "ok") {
    return { symbol: "⚠", className: "text-amber-600", label: "ok-other" }
  }
  if (row.outcome === "not_found") {
    return { symbol: "✗", className: "text-red-600", label: "not_found" }
  }
  // errored or anything unexpected
  return { symbol: "⚠", className: "text-amber-600", label: "errored" }
}

export default function ResultsTable({ summary, results, inputOrder }) {
  const { t } = useTranslation()
  const [modalRow, setModalRow] = useState(null)

  const ordered = orderResults(results, inputOrder)

  return (
    <div className="mt-6">
      <div className="mb-3 text-sm text-gray-700" data-testid="nfe-summary">
        {t("natura_operations.nfe_status.summary", {
          total: summary?.total ?? ordered.length,
          ok: summary?.ok ?? 0,
          errored: summary?.errored ?? 0,
          not_found: summary?.not_found ?? 0,
        })}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 w-8" aria-label="icon" />
              <th className="px-3 py-2 font-medium">{t("natura_operations.nfe_status.col_order")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.nfe_status.col_status")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.nfe_status.col_action")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.nfe_status.col_description")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.nfe_status.col_message")}</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((row) => {
              const icon = outcomeIcon(row)
              const statusLabel =
                row.outcome === "not_found"
                  ? t("natura_operations.nfe_status.outcome_not_found_short")
                  : row.outcome === "errored"
                    ? t("natura_operations.nfe_status.outcome_errored_short")
                    : row.status_code || "—"
              const message = displayedMessage(row)
              const openModal = () => setModalRow(row)

              return (
                <tr
                  key={row.order_number}
                  onClick={openModal}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      openModal()
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={t("natura_operations.nfe_status.row_aria_label", { order_number: row.order_number })}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid={`nfe-row-${row.order_number}`}
                >
                  <td className={`px-3 py-2 text-center font-bold ${icon.className}`} aria-label={icon.label}>
                    {icon.symbol}
                  </td>
                  <td className="px-3 py-2 font-mono">{row.order_number}</td>
                  <td className="px-3 py-2">{statusLabel}</td>
                  <td className="px-3 py-2">{row.action ?? "—"}</td>
                  <td className="px-3 py-2">{row.description ?? "—"}</td>
                  <td className="px-3 py-2 break-words max-w-xs" title={message}>
                    {truncate(message, 40)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <MsgModal open={!!modalRow} onClose={() => setModalRow(null)} row={modalRow} />
    </div>
  )
}

ResultsTable.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    ok: PropTypes.number,
    errored: PropTypes.number,
    not_found: PropTypes.number,
  }),
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  inputOrder: PropTypes.arrayOf(PropTypes.string),
}

ResultsTable.defaultProps = {
  summary: null,
  inputOrder: [],
}
